import { Grid } from '@mui/material';
import WidgetCard, { WidgetCardProps } from './WidgetCard';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useContext, SetStateAction, Dispatch } from 'react';
import { client } from '../../../../app/services/api/orderManagementClient';
import { UserContext } from '../../../../components/shared/useUser';
import { getAppInsightsInfo } from '../../../../app/services/environment';
import useNswagClient from '../../../../../src/hooks/api/useNswagClient';
import { PodWidgetType } from '../../../../app/services/api/generated';
import useLogError from '../../../../../src/hooks/useLogError';
import useCurrencyCodeConfiguration from '../../../../hooks/configurations/useCurrencyCodeConfiguration';
import { applyRoundAndFormat } from '../../../../utils/index';

const AIOrderingWidgets = () => {
  const { t } = useTranslation('common');
  const { selectedStore } = useContext(UserContext);
  const { getPodWidgetData } = useNswagClient();
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const [meanErrorData, setMeanErrorData] = useState<WidgetCardProps>({ isLoading:true, isSuccessful: false });
  const [aiMapeData, setAiMapeData] = useState<WidgetCardProps>({ isLoading:true, isSuccessful: false });
  const [stockoutsData, setStockoutsData] = useState<WidgetCardProps>({ isLoading:true, isSuccessful: false });
  const [orderingTimeData, setOrderingTimeData] = useState<WidgetCardProps>({ isLoading:true, isSuccessful: false });
  const [wasteData, setWasteData] = useState<WidgetCardProps>({ isLoading:true, isSuccessful: false });
  const [missedSalesData, setMissedSalesData] = useState<WidgetCardProps>({ isLoading:true, isSuccessful: false });
  const { logError } = useLogError();

  const fetchMeanErrorData = async () => {
    return fetchIMWidgetData(1, t('reporting.widget-titles.mean-error'), t('reporting.widget-tooltips.mean-error'), setMeanErrorData);
  };

  const fetchAiMapeData  = async () => {
    return fetchIMWidgetData(0, t('reporting.widget-titles.ai-mape'), t('reporting.widget-tooltips.ai-mape'), setAiMapeData);
  };

  const fetchStockoutsData = async () => {
    return fetchIMWidgetData(2, t('reporting.widget-titles.weekly-stockouts'), t('reporting.widget-tooltips.weekly-stockouts'), setStockoutsData);
  };

  const fetchWasteData = async () => {
    return fetchIMWidgetData(3, t('reporting.widget-titles.waste-value'), t('reporting.widget-tooltips.waste-value'), setWasteData);
  };

  const fetchMisseedSalesData = async () => {
    return fetchIMWidgetData(4, t('reporting.widget-titles.missed-sales'), t('reporting.widget-tooltips.missed-sales'), setMissedSalesData);
  };

  const fetchOrderingTimeData = async () => {
    const appInfo = getAppInsightsInfo();

    client.getTimeTrackingWidgetData(`${selectedStore?.storeNumber}`, appInfo.applicationId).then((result) => {
      if (!result.success) {
        setOrderingTimeData({
          isLoading : false,
          isSuccessful : false,
        });
      }
      setOrderingTimeData({
        isLoading : false,
        isSuccessful : true,
        title : t('reporting.widget-titles.ordering-time'),
        tooltipText: t('reporting.widget-tooltips.ordering-time'),
        value: result.data.Value,
        progressPercentage: result.data.ProgressPercentage === 0 ? undefined : result.data.ProgressPercentage,
        changeValue: result.data.ChangeFromPreviousPeriodPercentage,
        usesProgressBar: true,
      });
    }).catch((error) => {
      logError(error);
      setOrderingTimeData({
        isLoading : false,
        isSuccessful : false,
      });
    });
  };

  const fetchIMWidgetData = async (type: PodWidgetType, title: string, tooltip: string,setState: Dispatch<SetStateAction<WidgetCardProps>>) => {
    getPodWidgetData(type , `${selectedStore?.storeNumber}`).then((result)=>{
      setState({
        isLoading : false,
        isSuccessful : true,
        title : title,
        tooltipText: tooltip,
        value: type == 3 || type == 4 ? applyRoundAndFormat(parseFloat(`${result.value}`), currencyCode) : result.value,
        progressPercentage: result.progressPercentage,
        changeValue: result.changeFromPreviousPeriodPercentage,
        usesProgressBar: type != 2,
      });
    }).catch((err)=>{
      logError(err);
      setState({
        isLoading : false,
        isSuccessful : false,
      });
    });
  };

  useEffect(() => {
    getCurrencyCodeConfiguration();
  }, []);

  useEffect(() => {
    if (!currencyCode) return;

    Promise.all([
      fetchMeanErrorData(),
      fetchAiMapeData(),
      fetchStockoutsData(),
      fetchOrderingTimeData(),
      fetchWasteData(),
      fetchMisseedSalesData(),
    ]);
  }, [currencyCode]);

  return (
    <Grid
      container
      spacing={2}
      justifyContent="center"
      sx={{ maxWidth: 840, margin: '0 auto' }}
    >
      <Grid item>
        <WidgetCard {...meanErrorData} />
      </Grid>
      <Grid item>
        <WidgetCard {...aiMapeData} />
      </Grid>
      <Grid item>
        <WidgetCard {...stockoutsData} />
      </Grid>
      <Grid item>
        <WidgetCard {...orderingTimeData} />
      </Grid>
      <Grid item>
        <WidgetCard {...wasteData} />
      </Grid>
      <Grid item>
        <WidgetCard {...missedSalesData} />
      </Grid>
    </Grid>
  );
};

export default AIOrderingWidgets;
